import React, { useContext, useEffect, useState } from "react"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import NoSsr from "@material-ui/core/NoSsr"
import Plotly from "../components/plotly"
import _ from "lodash"
import { DataContextProvider, DataContext } from "./crossfilter-context"
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Card,
  Grid,
  MenuProps,
  MenuItem,
  Input,
  TextField,
} from "@material-ui/core"
import { DatePicker } from "@material-ui/pickers"
import DateFnsAdapter from "@material-ui/pickers/adapter/date-fns"
import enLocale from "date-fns/locale/en-US"
import moment from "moment"
import Loadable from "react-loadable"

const ReactJson = Loadable({
  loader: () => import(`react-json-view`),
  loading: ({ timedOut }) =>
    timedOut ? (
      <blockquote>Error: Loading react-json-view timed out.</blockquote>
    ) : (
      <blockquote>Loading...</blockquote>
    ),
  timeout: 10000,
})

function unpack(rows, key) {
  return rows.map(function (row) {
    return row[key]
  })
}

const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
    maxWidth: 300,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
  },
  chip: {
    margin: 2,
  },
  noLabel: {
    marginTop: theme.spacing(3),
  },
}))

const standardTransforms = ({ data, dates, datesEmpty, selectedStates }) => {
  return [
    {
      enabled: !datesEmpty(),
      type: "filter",
      target: unpack(data, "dateChecked"),
      operation: "()",
      value: dates,
    },
    {
      enabled: !_.isEmpty(selectedStates),
      type: "filter",
      target: unpack(data, "state"),
      operation: "{}",
      value: selectedStates,
    },
  ]
}

const standardWithAggregate = ({
  xProperty,
  data,
  dates,
  datesEmpty,
  selectedStates,
}) => {
  const baseTransforms = standardTransforms({
    data,
    dates,
    datesEmpty,
    selectedStates,
  })
  const aggregation = {
    type: "aggregate",
    groups: unpack(data, xProperty),
    aggregations: [{ target: "y", func: "sum" }],
  }
  const transforms = [...baseTransforms, aggregation]
  return transforms
}

const DataSampleJson = () => {
  const {
    data: { data },
  } = useContext(DataContext)

  return (
    <NoSsr>
      <ReactJson src={data.slice(-10, -1)} />
    </NoSsr>
  )
}

const SimpleSingleChart = ({ xProperty, yProperty, titleText }) => {
  const {
    data: { data },
    selectedStates,
    onDateRelayout,
    resetDates,
    dates,
    datesEmpty,
  } = useContext(DataContext)

  const dateDiff = moment(dates[0]).diff(moment(dates[1]), "days")

  const prevData = [
    {
      type: "scatter",
      mode: "lines",
      name: `${dateDiff} days`,
      x: unpack(data, xProperty),
      y: unpack(data, yProperty),
      opacity: 0.4,
      transforms: standardWithAggregate({
        xProperty,
        data,
        dates: dates.map(it =>
          moment(it).add(dateDiff, "days").format("YYYY-MM-DD")
        ),
        datesEmpty,
        selectedStates,
      }),
      line: {
        color: "rgb(31, 119, 180)",
      },
      simplify: true,
      smoothing: 1,
      shape: "spline",
      xaxis: "x2",
      hoverinfo: "skip",
    },
    {
      type: "scatter",
      mode: "lines",
      name: `${dateDiff} days`,
      x: unpack(data, xProperty),
      y: unpack(data, yProperty),
      opacity: 0.2,
      transforms: standardWithAggregate({
        xProperty,
        data,
        dates: dates.map(it =>
          moment(it)
            .add(2 * dateDiff, "days")
            .format("YYYY-MM-DD")
        ),
        datesEmpty,
        selectedStates,
      }),
      line: {
        color: "rgb(31, 119, 180)",
      },
      simplify: true,
      smoothing: 1,
      shape: "spline",
      xaxis: "x3",
      hoverinfo: "skip",
    },
  ]

  return (
    <NoSsr>
      <Plotly
        data={[
          {
            type: "scatter",
            mode: "lines",
            name: "Current",
            x: unpack(data, xProperty),
            y: unpack(data, yProperty),
            transforms: standardWithAggregate({
              xProperty,
              data,
              dates,
              datesEmpty,
              selectedStates,
            }),
            line: {
              color: "rgb(31, 119, 180)",
            },
            simplify: true,
            smoothing: 1,
            shape: "spline",
          },
          ...prevData,
        ]}
        layout={{
          paper_bgcolor: `rgba(0, 0, 0, 0)`,
          plot_bgcolor: `rgba(0, 0, 0, 0)`,
          transition: {
            duration: 400,
            easing: "cubic-in-out",
          },
          hovermode: "x unified",
          showlegend: false,
          yaxis: {
            atuomargin: true,
            fixedrange: true,
            rangemode: "tozero",
          },
          xaxis2: {
            overlaying: "x",
            showgrid: false,
            visible: false,
            layer: "below traces",
          },
          xaxis3: {
            overlaying: "x",
            showgrid: false,
            visible: false,
            layer: "below traces",
          },
          title: {
            text: titleText,
          },
          margin: {
            l: 50,
            r: 30,
            b: 30,
            t: 50,
            pad: 0,
          },
        }}
        style={{ width: `100%`, height: "100%" }}
        useResizeHandler
        config={{
          displayModeBar: false,
          showTips: false,
        }}
        onRelayout={onDateRelayout}
        onDoubleClick={resetDates}
      />
    </NoSsr>
  )
}

const TestPercentages = () => {
  const {
    data: { data },
    selectedStates,
    resetDates,
    onDateRelayout,
    dates,
    datesEmpty,
  } = useContext(DataContext)

  const filtered = data
    .filter(d =>
      datesEmpty() ? true : d.dateCheckedMoment.isBetween(dates[0], dates[1])
    )
    .filter(d =>
      _.isEmpty(selectedStates) ? true : selectedStates.includes(d.state)
    )
  const grouped = _.groupBy(filtered, "dateChecked")
  const reduced = _.reduce(
    grouped,
    (res, n, key) => {
      const total = _.sumBy(n, "totalTestResultsIncrease")
      const positiveIncrease = _.sumBy(n, "positiveIncrease")
      const negativeIncrease = _.sumBy(n, "negativeIncrease")
      res.push({
        dateChecked: key,
        total,
        positiveIncrease,
        negativeIncrease,
        positivePercent: _.clamp(positiveIncrease / total, 0, 1),
        negativePercent: _.clamp(negativeIncrease / total, 0, 1),
      })
      return res
    },
    []
  )
  const percentages = reduced

  const baseTransforms = standardTransforms({
    data,
    dates,
    datesEmpty,
    selectedStates,
  })
  const transforms = [
    ...baseTransforms,
    {
      type: "aggregate",
      groups: unpack(data, "dateChecked"),
      aggregations: [{ target: "y", func: "sum" }],
    },
  ]

  return (
    <NoSsr>
      <Plotly
        data={[
          {
            type: "scatter",
            mode: "lines",
            name: "% Negative",
            x: unpack(percentages, "dateChecked"),
            y: unpack(percentages, "negativePercent"),
            hovertemplate: "%{y:.1%}",
          },
          {
            type: "scatter",
            mode: "lines",
            name: "% Positive",
            x: unpack(percentages, "dateChecked"),
            y: unpack(percentages, "positivePercent"),
            hovertemplate: "%{y:.1%}",
          },
          {
            type: "scatter",
            mode: "lines",
            name: "Test Result Increase",
            x: unpack(data, "dateChecked"),
            y: unpack(data, "totalTestResultsIncrease"),
            yaxis: "y2",
            transforms: standardWithAggregate({
              xProperty: "dateChecked",
              data,
              dates,
              datesEmpty,
              selectedStates,
            }),
          },
        ]}
        layout={{
          paper_bgcolor: `rgba(0, 0, 0, 0)`,
          plot_bgcolor: `rgba(0, 0, 0, 0)`,
          autosize: true,
          transition: {
            duration: 400,
            easing: "cubic-in-out",
          },
          showlegend: true,
          legend: {
            orientation: "h",
            yanchor: "bottom",
            xanchor: "left",
            y: -0.2,
          },
          xAxis: {
            rangeslider: {},
          },
          yAxis: {
            fixedrange: true,
            atuomargin: true,
            title: "%",
            side: "left",
            tickformat: ",.0%",
            rangemode: "tozero",
            range: [0, 1],
          },
          yaxis2: {
            overlaying: "y",
            fixedrange: true,
            atuomargin: true,
            showgrid: false,
            side: "right",
            rangemode: "nonnegative",
            // title: "Counts",
          },
        }}
        style={{ width: `100%` }}
        useResizeHandler
        config={{
          displayModeBar: false,
          showTips: false,
        }}
        onRelayout={onDateRelayout}
        onDoubleClick={resetDates}
      />
    </NoSsr>
  )
}

const CountIndicator = ({ title, propertyName }) => {
  const {
    data: { data },
    selectedStates,
    dates,
    datesEmpty,
  } = useContext(DataContext)

  const filtered = data
    .filter(d =>
      datesEmpty() ? true : d.dateCheckedMoment.isBetween(dates[0], dates[1])
    )
    .filter(d =>
      _.isEmpty(selectedStates) ? true : selectedStates.includes(d.state)
    )
  const series = unpack(filtered, propertyName)
  const maxDate = _.maxBy(filtered, d => d.dateCheckedMoment)
  const total = _.sum(series)
  return (
    <NoSsr>
      <Plotly
        data={[
          {
            type: "indicator",
            mode: "number+delta",
            value: total,
            delta: {
              reference: total - maxDate[propertyName],
              // relative: true,
              position: "bottom",
            },
            title: title
              ? {
                  text: title,
                }
              : undefined,
          },
        ]}
        layout={{
          autosize: true,
          height: 225,
          margin: { t: 25, r: 25, l: 25, b: 25 },
        }}
        style={{ width: "100%" }}
        useResizeHandler
      />
    </NoSsr>
  )
}

const DeathsOverTime = () => {
  const {
    data: { data },
    selectedStates,
  } = useContext(DataContext)

  const transforms = [
    {
      enabled: !_.isEmpty(selectedStates),
      type: "filter",
      target: unpack(data, "state"),
      operation: "{}",
      value: selectedStates,
    },
    {
      type: "aggregate",
      groups: unpack(data, "dateChecked"),
      aggregations: [{ target: "y", func: "sum" }],
    },
  ]

  return (
    <NoSsr>
      <Plotly
        data={[
          {
            type: "scatter",
            mode: "lines+markers",
            name: "Deaths",
            x: unpack(data, "dateChecked"),
            y: unpack(data, "deathIncrease"),
            transforms,
            yaxis: "y2",
          },
          {
            type: "scatter",
            mode: "lines+markers",
            name: "Positive Cases",
            x: unpack(data, "dateChecked"),
            y: unpack(data, "positiveIncrease"),
            transforms,
          },
        ]}
        layout={{
          paper_bgcolor: `rgba(0, 0, 0, 0)`,
          plot_bgcolor: `rgba(0, 0, 0, 0)`,
          autosize: true,
          transition: {
            duration: 400,
            easing: "cubic-in-out",
          },
          yaxis2: {
            overlaying: "y",
            fixedrange: true,
            side: "right",
            // title: "Counts",
          },
        }}
        style={{ width: `100%` }}
        useResizeHandler
        config={{
          displayModeBar: false,
          showTips: false,
        }}
      />
    </NoSsr>
  )
}

const DateRangeComponent = () => {
  const { dates, setDates, resetDates } = useContext(DataContext)
  return (
    <>
      <DatePicker
        label="From"
        value={dates[0]}
        onChange={newValue => setDates([newValue, dates[1]])}
        renderInput={props => <TextField {...props} />}
        dateAdapter={new DateFnsAdapter({ locale: enLocale })}
        clearable
        disableFuture
      />
      <div>-</div>
      <DatePicker
        label="To"
        value={dates[1]}
        onChange={newValue => setDates([dates[0], newValue])}
        renderInput={props => <TextField {...props} />}
        dateAdapter={new DateFnsAdapter({ locale: enLocale })}
        clearable
        disableFuture
      />

      <button
        onClick={() =>
          setDates([
            moment().add(-31, "days").format("YYYY-MM-DD"),
            moment().format("YYYY-MM-DD"),
          ])
        }
      >
        last 30
      </button>
      <button
        onClick={() =>
          setDates([
            moment().add(-60, "days").format("YYYY-MM-DD"),
            moment().format("YYYY-MM-DD"),
          ])
        }
      >
        last 60
      </button>
      <button onClick={resetDates}>clear</button>
    </>
  )
}

const StatesDropdown = () => {
  const {
    data: { data },
    selectedStates,
    setSelectedStates,
  } = useContext(DataContext)
  const classes = useStyles()
  const [availableStates, setAvailableStates] = useState([])

  useEffect(() => {
    if (!data) return

    const states = Array.from(new Set(data.map(d => d.state)))
    states.sort()
    setAvailableStates(["All", ...states])
  }, [data])

  const handleChange = e => {
    if (e.target.value === "All") {
      setSelectedStates([])
    } else {
      setSelectedStates(e.target.value)
    }
  }

  const stateOptions = availableStates.map(name => (
    <MenuItem key={name} value={name}>
      {name}
    </MenuItem>
  ))

  return (
    <>
      <FormControl className={classes.formControl}>
        <InputLabel id="multiple-state-label">State</InputLabel>
        <Select
          labelId="multiple-state-label"
          value={selectedStates}
          onChange={handleChange}
          input={<Input id="select-multiple-chip" />}
          // multiple
          // renderValue={selected => (
          //   <div className={classes.chips}>
          //     {selected.map(value => (
          //       <Chip key={value} label={value} className={classes.chip} />
          //     ))}
          //   </div>
          // )}
          MenuProps={MenuProps}
        >
          {stateOptions}
        </Select>
      </FormControl>
    </>
  )
}

const ChartsSelfFiltered = () => {
  return (
    <>
      <Box>
        {/*<Divider variant="middle" />*/}
        <Grid
          container
          direction="row"
          justify="space-around"
          alignItems="center"
          spacing={3}
        >
          <Grid item xs={12}>
            <Card
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-evenly",
                flexWrap: "wrap",
              }}
            >
              <StatesDropdown />
              <DateRangeComponent />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card style={{ height: "200px" }}>
              <CountIndicator
                title="Positive Tests"
                propertyName="positiveIncrease"
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card style={{ height: "200px" }}>
              <CountIndicator title="Deaths" propertyName="deathIncrease" />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card style={{ height: "200px" }}>
              <CountIndicator
                title="Negative Tests"
                propertyName="negativeIncrease"
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card style={{ height: "200px", padding: "5px" }}>
              <SimpleSingleChart
                xProperty="dateChecked"
                yProperty="positiveIncrease"
                titleText="Positive Tests"
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card style={{ height: "200px", padding: "5px" }}>
              <SimpleSingleChart
                xProperty="dateChecked"
                yProperty="deathIncrease"
                titleText="Deaths"
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card style={{ height: "200px", padding: "5px" }}>
              <SimpleSingleChart
                xProperty="dateChecked"
                yProperty="negativeIncrease"
                titleText="Negative Tests"
              />
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <TestPercentages />
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card style={{ height: "300px", padding: "15px" }}>
              <SimpleSingleChart
                xProperty="dateChecked"
                yProperty="hospitalizedCurrently"
                titleText="Hospitalizations"
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card style={{ height: "300px", padding: "15px" }}>
              <SimpleSingleChart
                xProperty="dateChecked"
                yProperty="inIcuCurrently"
                titleText="In ICU"
              />
            </Card>
          </Grid>
          {/*<Grid item xs={12}>*/}
          {/*  <DataSampleJson />*/}
          {/*</Grid>*/}
        </Grid>
      </Box>
    </>
  )
}

export default () => {
  return (
    <DataContextProvider>
      <ChartsSelfFiltered />
    </DataContextProvider>
  )
}
